/**
 * WebShellLayout — the authenticated layout route.
 *
 * Wraps every in-app route in `<AuthGuard>` + the web `WebAppShell` and
 * renders the matched child route into the shell body via `<Outlet />`.
 * Owns the cross-route concerns that used to live in `Dashboard`:
 *   - identity (`useMe`) for the profile menu
 *   - the logout mutation
 *   - brand-click → `/dashboard`
 *   - nav selection → `navigate(path)` (the active item is derived from
 *     the URL inside `WebLeftMenu`, so no `activeNavId` is threaded)
 *
 * Routing the shell at the layout level (rather than per page) keeps the
 * sidebar mounted across navigations and makes active-from-URL the single
 * source of truth for the highlight.
 */
import { Outlet, useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMe } from "@nu-assist/shared-fe";
import { AuthGuard } from "../shared/AuthGuard";
import { WebAppShell } from "./WebAppShell";
import { clearStoredSelectedImport } from "../features/imports/selection/SelectedImportContext";
import { MEDIA_SESSION_QUERY_KEY } from "../features/media/MediaSessionProvider";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

function ShellWithIdentity() {
  const me = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: async () => {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      if (!response.ok) throw new Error(`logout ${response.status}`);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
    },
    // onSettled, not onSuccess: a logout POST that 5xx's or times out still
    // ends the session from the user's point of view, and leaving another
    // recruiter's filename / owner / linked search behind is exactly the leak
    // this clear exists to prevent.
    onSettled: () => {
      clearStoredSelectedImport();
      // The API cleared the avatars' CloudFront cookies; drop the session that
      // said they were valid, so nothing builds /media/avatars URLs after this.
      void queryClient.resetQueries({ queryKey: MEDIA_SESSION_QUERY_KEY });
    },
  });

  // AuthGuard guarantees authenticated state by render time; defensive
  // narrow for TS.
  if (me.status !== "authenticated") return null;

  const fullName =
    [me.data.first_name, me.data.last_name].filter(Boolean).join(" ") || me.data.email;

  const role = [me.data.role, me.data.department].filter(Boolean).join(" · ") || undefined;

  return (
    <WebAppShell
      user={{ name: fullName, email: me.data.email, role }}
      onHome={() => navigate("/dashboard")}
      onNavSelect={(path) => navigate(path)}
      onLogout={() => logout.mutate()}
    >
      <Outlet />
    </WebAppShell>
  );
}

export function WebShellLayout() {
  return (
    <AuthGuard>
      <ShellWithIdentity />
    </AuthGuard>
  );
}
